/**
 * 構造化ロガー
 * 
 * 開発環境: すべてのログレベルを出力
 * 本番環境: ERROR のみ出力
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * 機密情報として扱うキー
 */
const SENSITIVE_KEYS = [
  'password',
  'token',
  'apiKey',
  'api_key',
  'authorization',
  'authorizationHeader',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'creditCard',
  'credit_card',
  'ssn',
  'socialSecurityNumber',
  'secret',
  'secretKey',
  'secret_key',
]

/**
 * 機密情報をマスキングする
 */
function maskSensitiveData(data: unknown): unknown {
  if (data === null || data === undefined) {
    return data
  }

  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return data
  }

  if (Array.isArray(data)) {
    return data.map(maskSensitiveData)
  }

  if (typeof data === 'object') {
    const masked: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase()
      if (SENSITIVE_KEYS.some(sk => lowerKey.includes(sk.toLowerCase()))) {
        masked[key] = '***REDACTED***'
      } else if (typeof value === 'object' && value !== null) {
        masked[key] = maskSensitiveData(value)
      } else {
        masked[key] = value
      }
    }
    return masked
  }

  return data
}

/**
 * 環境変数からログレベルを取得
 */
function getLogLevelFromEnv(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL?.toLowerCase()
  
  if (envLevel === 'debug') return LogLevel.DEBUG
  if (envLevel === 'info') return LogLevel.INFO
  if (envLevel === 'warn') return LogLevel.WARN
  if (envLevel === 'error') return LogLevel.ERROR
  
  // デフォルト: 開発環境は INFO、本番環境は ERROR
  return import.meta.env.DEV ? LogLevel.INFO : LogLevel.ERROR
}

/**
 * リクエストIDを取得（requestId.ts からインポート）
 */
import { getRequestId as getRequestIdFn } from './requestId'

const getRequestId = getRequestIdFn

/**
 * ログエントリの構造
 */
interface LogEntry {
  timestamp: string
  level: string
  message: string
  requestId?: string | null
  data?: Record<string, unknown>
  error?: {
    message: string
    stack?: string
    name?: string
  }
}

/**
 * ログエントリをフォーマット
 */
function formatLogEntry(
  level: string,
  message: string,
  data?: Record<string, unknown>,
  error?: Error | unknown
): LogEntry {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    message,
  }

  // リクエストIDを追加
  if (getRequestId) {
    entry.requestId = getRequestId()
  }

  // データを追加（機密情報をマスキング）
  if (data) {
    entry.data = maskSensitiveData(data) as Record<string, unknown>
  }

  // エラー情報を追加
  if (error) {
    if (error instanceof Error) {
      entry.error = {
        message: error.message,
        stack: error.stack,
        name: error.name,
      }
    } else {
      entry.error = {
        message: String(error),
      }
    }
  }

  return entry
}

/**
 * ロガークラス
 */
class Logger {
  private readonly isDevelopment: boolean
  private readonly logLevel: LogLevel

  constructor() {
    this.isDevelopment = import.meta.env.DEV
    this.logLevel = getLogLevelFromEnv()
  }

  /**
   * ログを出力するかどうかを判定
   */
  private shouldLog(level: LogLevel): boolean {
    // 本番環境では ERROR のみ出力
    if (!this.isDevelopment && level !== LogLevel.ERROR) {
      return false
    }

    // ログレベルが設定値以上の場合のみ出力
    return level >= this.logLevel
  }

  /**
   * DEBUG ログ
   */
  debug(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return

    const entry = formatLogEntry('DEBUG', message, data)
    console.debug(JSON.stringify(entry))
  }

  /**
   * INFO ログ
   */
  info(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.INFO)) return

    const entry = formatLogEntry('INFO', message, data)
    console.info(JSON.stringify(entry))
  }

  /**
   * WARN ログ
   */
  warn(message: string, data?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.WARN)) return

    const entry = formatLogEntry('WARN', message, data)
    console.warn(JSON.stringify(entry))
  }

  /**
   * ERROR ログ
   */
  error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.ERROR)) return

    const entry = formatLogEntry('ERROR', message, data, error)
    console.error(JSON.stringify(entry))
    
    // エラーオブジェクトがある場合はスタックトレースも出力
    if (error instanceof Error && this.isDevelopment) {
      console.error(error)
    }
  }
}

/**
 * シングルトンインスタンス
 */
let loggerInstance: Logger | null = null

/**
 * ロガーインスタンスを取得
 */
export function getLogger(): Logger {
  if (!loggerInstance) {
    loggerInstance = new Logger()
  }
  return loggerInstance
}

/**
 * デフォルトエクスポート
 */
export default getLogger()
